import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  applyPayment,
  completePayflowSession,
  createPayflowSession,
  previewPayment,
  type CreatePayflowSessionResponse,
  type PaymentPreviewResponse,
} from '../../endpoints/loans';
import { getApiErrorMessage } from '../../utils/httpClientTs';
import PayflowHostedCardFrame, {
  type PayflowHostedMessage,
} from '../../Components/PayflowHostedCardFrame';

type PaymentMethod = 'check' | 'card';

type ElfLoanPaymentDialogProps = {
  open: boolean;
  onClose: () => void;
  borrowerUserId: string;
  currency?: string;
  onSuccess: () => void;
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const ElfLoanPaymentDialog: React.FC<ElfLoanPaymentDialogProps> = ({
  open,
  onClose,
  borrowerUserId,
  currency = 'USD',
  onSuccess,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('check');
  const [amount, setAmount] = useState('');
  const [postingDate, setPostingDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [checkNumber, setCheckNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [previewPending, setPreviewPending] = useState(false);
  const [preview, setPreview] = useState<PaymentPreviewResponse | null>(null);
  const [payflowSession, setPayflowSession] =
    useState<CreatePayflowSessionResponse | null>(null);
  const [cardIntentPending, setCardIntentPending] = useState(false);
  const [cardCompleting, setCardCompleting] = useState(false);
  const finalizeInFlightRef = useRef(false);

  const resetForm = () => {
    setPaymentMethod('check');
    setAmount('');
    setPostingDate(new Date().toISOString().slice(0, 10));
    setCheckNumber('');
    setError(null);
    setPending(false);
    setPreviewPending(false);
    setPreview(null);
    setPayflowSession(null);
    setCardIntentPending(false);
    setCardCompleting(false);
    finalizeInFlightRef.current = false;
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    setPayflowSession(null);
    setError(null);
    setPreview(null);
  }, [paymentMethod]);

  const parsedAmount = Number(amount);
  const canPay = parsedAmount > 0 && postingDate.trim() !== '';
  const previewMatchesInput =
    !!preview &&
    preview.amount === parsedAmount &&
    preview.posting_date === postingDate;

  const clearPreview = () => {
    setPreview(null);
    setPayflowSession(null);
  };

  const handleClose = () => {
    if (pending || cardIntentPending || previewPending || cardCompleting)
      return;
    onClose();
  };

  const handleReview = async () => {
    setError(null);
    if (!canPay) {
      setError('Enter a valid amount and posting date.');
      return;
    }
    if (paymentMethod === 'check' && !checkNumber.trim()) {
      setError('Check or reference number is required.');
      return;
    }
    setPreviewPending(true);
    try {
      const data = await previewPayment({
        user_id: borrowerUserId,
        amount: parsedAmount,
        posting_date: postingDate,
      });
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setError(getApiErrorMessage(err));
    } finally {
      setPreviewPending(false);
    }
  };

  const handleSubmitCheck = async () => {
    setError(null);
    const ref = checkNumber.trim();
    if (!ref) {
      setError('Check or reference number is required.');
      return;
    }
    if (!canPay) {
      setError('Enter a valid amount and posting date.');
      return;
    }
    if (!previewMatchesInput) {
      setError('Review the payment preview before applying.');
      return;
    }
    setPending(true);
    try {
      await applyPayment({
        user_id: borrowerUserId,
        amount: parsedAmount,
        posting_date: postingDate,
        payment_method: 'check',
        payment_ref: ref,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const handlePrepareCard = async () => {
    setError(null);
    setPayflowSession(null);
    if (!canPay) {
      setError('Enter a valid amount and posting date.');
      return;
    }
    if (!previewMatchesInput) {
      setError('Review the payment preview before continuing to card entry.');
      return;
    }
    setCardIntentPending(true);
    try {
      const data = await createPayflowSession({
        user_id: borrowerUserId,
        purpose: 'payment',
        amount: parsedAmount,
        posting_date: postingDate,
      });
      setPayflowSession(data);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setCardIntentPending(false);
    }
  };

  const finalizePayflowPayment = async () => {
    if (!payflowSession || finalizeInFlightRef.current) return;
    finalizeInFlightRef.current = true;
    setCardCompleting(true);
    setError(null);
    try {
      await completePayflowSession(payflowSession.session_id);
      onSuccess();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setCardCompleting(false);
      finalizeInFlightRef.current = false;
    }
  };

  const onPayflowMessage = (msg: PayflowHostedMessage) => {
    if (msg.status === 'approved') {
      void finalizePayflowPayment();
      return;
    }
    if (msg.status === 'canceled') {
      setError('Card entry was canceled.');
      setPayflowSession(null);
      return;
    }
    setError('Card was not approved. You can try again.');
    setPayflowSession(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Make payment</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Payment applies across this borrower&apos;s active loans ({currency}).
          Review the allocation before applying.
        </Typography>

        <Stack spacing={2}>
          <ToggleButtonGroup
            exclusive
            value={paymentMethod}
            onChange={(_, value) => value && setPaymentMethod(value)}
            size="small"
            fullWidth
          >
            <ToggleButton value="check">Check</ToggleButton>
            <ToggleButton value="card">Card (PayPal)</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Amount"
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 0.01, step: 0.01 }}
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              clearPreview();
            }}
          />
          <TextField
            label="Posting date"
            type="date"
            size="small"
            fullWidth
            value={postingDate}
            onChange={(e) => {
              setPostingDate(e.target.value);
              clearPreview();
            }}
            InputLabelProps={{ shrink: true }}
          />

          {paymentMethod === 'check' && (
            <TextField
              label="Check or reference number"
              size="small"
              fullWidth
              required
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
            />
          )}

          <Button
            variant="outlined"
            disabled={!canPay || previewPending || pending}
            onClick={() => void handleReview()}
          >
            {previewPending ? 'Reviewing…' : 'Review payment'}
          </Button>

          {previewMatchesInput && preview && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Payment preview
              </Typography>
              <Stack spacing={0.75}>
                <PreviewRow
                  label="Charges"
                  value={fmtMoney(preview.total_applied_to_charges)}
                />
                <PreviewRow
                  label="Interest due"
                  value={fmtMoney(preview.total_applied_to_interest)}
                />
                <PreviewRow
                  label="Principal"
                  value={fmtMoney(preview.total_applied_to_principal)}
                />
                {preview.total_accrued_deferred > 0 && (
                  <PreviewRow
                    label="Accrued (this payment)"
                    value={fmtMoney(preview.total_accrued_deferred)}
                  />
                )}
                {preview.has_payoff_billing && (
                  <PreviewRow
                    label="Accrued billed on payoff"
                    value={fmtMoney(preview.total_deferred_billed_on_payoff)}
                  />
                )}
                {preview.total_applied_to_interest_after_bill > 0 && (
                  <PreviewRow
                    label="Applied to billed accrued"
                    value={fmtMoney(
                      preview.total_applied_to_interest_after_bill
                    )}
                  />
                )}
                {preview.unapplied_amount > 0 && (
                  <PreviewRow
                    label="Unapplied"
                    value={fmtMoney(preview.unapplied_amount)}
                  />
                )}
              </Stack>

              {preview.loans.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Per loan
                  </Typography>
                  <Stack spacing={1}>
                    {preview.loans.map((loan) => (
                      <Box key={loan.loan_id}>
                        <Typography variant="body2" fontWeight={600}>
                          {loan.loan_number
                            ? `Loan #${loan.loan_number}`
                            : `Loan ${loan.loan_id.slice(0, 8)}`}
                          {loan.would_pay_off ? ' · payoff' : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Charges {fmtMoney(loan.applied_to_charges)} · Interest{' '}
                          {fmtMoney(loan.applied_to_interest)} · Principal{' '}
                          {fmtMoney(loan.applied_to_principal)}
                          {loan.deferred_billed_on_payoff > 0
                            ? ` · Bill accrued ${fmtMoney(loan.deferred_billed_on_payoff)}`
                            : ''}
                          {loan.applied_to_interest_after_bill > 0
                            ? ` · After bill ${fmtMoney(loan.applied_to_interest_after_bill)}`
                            : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}

              {preview.warnings?.map((warning) => (
                <Alert key={warning} severity="warning" sx={{ mt: 1.5 }}>
                  {warning}
                </Alert>
              ))}
            </Box>
          )}

          {paymentMethod === 'card' && (
            <>
              {!payflowSession && (
                <Button
                  variant="contained"
                  disabled={
                    !canPay || !previewMatchesInput || cardIntentPending
                  }
                  onClick={() => void handlePrepareCard()}
                >
                  {cardIntentPending ? 'Preparing…' : 'Continue to card entry'}
                </Button>
              )}
              {payflowSession && (
                <PayflowHostedCardFrame
                  iframeUrl={payflowSession.iframe_url}
                  sessionId={payflowSession.session_id}
                  onMessage={onPayflowMessage}
                />
              )}
              {cardCompleting && (
                <Alert severity="info">Applying payment…</Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={
            pending || cardIntentPending || previewPending || cardCompleting
          }
        >
          Cancel
        </Button>
        {paymentMethod === 'check' && (
          <Button
            variant="contained"
            disabled={!canPay || !previewMatchesInput || pending}
            onClick={handleSubmitCheck}
          >
            {pending ? 'Applying…' : 'Apply payment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const PreviewRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

export default ElfLoanPaymentDialog;
